/**
 * Core Yahtzee Game Types
 *
 * All TypeScript types and interfaces for the Yahtzee dice game.
 * Standard Yahtzee rules: 5 dice, 13 scoring categories, 3 rolls per turn.
 */

// ============================================================================
// Basic Types
// ============================================================================

export type DieValue = 1 | 2 | 3 | 4 | 5 | 6;
export type YahtzeeDifficulty = 'easy' | 'medium' | 'hard';
export type YahtzeePlayerType = 'human' | 'ai';

// ============================================================================
// Dice Types
// ============================================================================

export interface Die {
  value: DieValue;
  held: boolean;
}

// ============================================================================
// Scoring Types
// ============================================================================

export type UpperCategory =
  | 'ones'
  | 'twos'
  | 'threes'
  | 'fours'
  | 'fives'
  | 'sixes';

export type LowerCategory =
  | 'threeOfAKind'
  | 'fourOfAKind'
  | 'fullHouse'
  | 'smallStraight'
  | 'largeStraight'
  | 'yahtzee'
  | 'chance';

export type ScoreCategory = UpperCategory | LowerCategory;

export const UPPER_CATEGORIES: UpperCategory[] = [
  'ones', 'twos', 'threes', 'fours', 'fives', 'sixes',
];

export const LOWER_CATEGORIES: LowerCategory[] = [
  'threeOfAKind', 'fourOfAKind', 'fullHouse',
  'smallStraight', 'largeStraight', 'yahtzee', 'chance',
];

export const ALL_CATEGORIES: ScoreCategory[] = [
  ...UPPER_CATEGORIES,
  ...LOWER_CATEGORIES,
];

export const CATEGORY_LABELS: Record<ScoreCategory, string> = {
  ones: 'Ones',
  twos: 'Twos',
  threes: 'Threes',
  fours: 'Fours',
  fives: 'Fives',
  sixes: 'Sixes',
  threeOfAKind: 'Three of a Kind',
  fourOfAKind: 'Four of a Kind',
  fullHouse: 'Full House',
  smallStraight: 'Small Straight',
  largeStraight: 'Large Straight',
  yahtzee: 'Yahtzee',
  chance: 'Chance',
};

// ============================================================================
// Scorecard Types
// ============================================================================

export interface Scorecard {
  scores: Partial<Record<ScoreCategory, number>>;
  yahtzeeBonusCount: number;
}

export interface ScoringResult {
  category: ScoreCategory;
  score: number;
  isValid: boolean;
}

// ============================================================================
// Player Types
// ============================================================================

export interface YahtzeePlayer {
  id: string;
  name: string;
  type: YahtzeePlayerType;
  difficulty?: YahtzeeDifficulty;
  scorecard: Scorecard;
}

// ============================================================================
// Round Types
// ============================================================================

export interface YahtzeeRound {
  roundNumber: number;
  playerIndex: number;
  dice: Die[];
  rollsRemaining: number;
  selectedCategory: ScoreCategory | null;
  score: number | null;
}

// ============================================================================
// Game State
// ============================================================================

export type YahtzeeGameStatus =
  | { type: 'setup' }
  | { type: 'active' }
  | { type: 'completed'; winner: string };

export interface YahtzeeGameState {
  gameId: string;
  players: YahtzeePlayer[];
  currentPlayerIndex: number;
  currentRound: number;
  dice: Die[];
  rollsRemaining: number;
  gameStatus: YahtzeeGameStatus;
  startedAt: number;
  lastActionAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface YahtzeeGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: YahtzeeDifficulty;
  playerCount: number; // 1-4
  aiOpponentCount?: number; // 1-3 (for single-player)
  playerNames?: string[];
  isPrivate?: boolean;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface YahtzeeGameResult {
  gameId: string;
  mode: YahtzeeGameSettings['mode'];
  difficulty?: YahtzeeDifficulty;
  playerCount: number;
  winner?: string;
  scores: Record<string, number>;
  totalRounds: number;
  yahtzeesRolled: number;
  duration: number;
  completedAt: number;
}

export interface YahtzeeGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageScore: number;
  highScore: number;
  totalYahtzees: number;
}

export interface YahtzeeStatistics {
  userId?: string;
  singlePlayer: {
    easy: YahtzeeGameStats;
    medium: YahtzeeGameStats;
    hard: YahtzeeGameStats;
  };
  localMultiplayer: YahtzeeGameStats;
  onlineMultiplayer: YahtzeeGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface YahtzeeGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const NUM_DICE = 5;
export const MAX_ROLLS = 3;
export const TOTAL_ROUNDS = 13;
export const UPPER_BONUS_THRESHOLD = 63;
export const UPPER_BONUS_SCORE = 35;
export const YAHTZEE_SCORE = 50;
export const YAHTZEE_BONUS_SCORE = 100;
export const FULL_HOUSE_SCORE = 25;
export const SMALL_STRAIGHT_SCORE = 30;
export const LARGE_STRAIGHT_SCORE = 40;
